import { useState } from "react";
import { type BlogPost } from "@/mocks/blogPosts";
import { useBlogPosts } from "@/hooks/useBlogPosts";
import BlogPostModal from "./BlogPostModal";

const categoryColors: Record<string, string> = {
  "Materials & Standards": "bg-teal-100 text-teal-700",
  "HVAC Systems": "bg-sky-100 text-sky-700",
  "Case Studies": "bg-amber-100 text-amber-700",
  "Compliance & Regulations": "bg-rose-100 text-rose-700",
  "Industry Trends": "bg-violet-100 text-violet-700",
  "How-To Guides": "bg-emerald-100 text-emerald-700",
};

interface BlogCardProps {
  post: BlogPost;
  featured?: boolean;
  onOpen: (post: BlogPost) => void;
}

function BlogCard({ post, featured, onOpen }: BlogCardProps) {
  return (
    <article
      className={`bg-white rounded-lg overflow-hidden border border-gray-100 hover:border-teal-200 transition-all duration-300 group cursor-pointer flex ${featured ? "flex-col md:flex-row" : "flex-col"}`}
      onClick={() => onOpen(post)}
    >
      <div className={`overflow-hidden ${featured ? "w-full md:w-[55%] h-[260px] md:h-auto" : "h-[220px]"}`}>
        <img
          src={post.image}
          alt={post.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className={`flex flex-col justify-between p-6 ${featured ? "md:w-[45%]" : ""}`}>
        <div>
          <div className="flex items-center gap-2 mb-3">
            <span className={`text-xs font-semibold px-2.5 py-1 rounded-md ${categoryColors[post.category] || "bg-gray-100 text-gray-600"}`}>
              {post.category}
            </span>
            <span className="text-xs text-gray-400">{post.readTime}</span>
          </div>
          <h2 className={`font-bold text-gray-900 leading-snug mb-3 group-hover:text-teal-700 transition-colors ${featured ? "text-xl md:text-2xl" : "text-base"}`} style={{ fontFamily: "'Playfair Display', serif" }}>
            {post.title}
          </h2>
          <p className="text-gray-500 text-sm leading-relaxed line-clamp-3">{post.excerpt}</p>
        </div>
        <div className="flex items-center justify-between mt-5 pt-4 border-t border-gray-100">
          <div className="flex items-center gap-2">
            <span className="w-7 h-7 flex items-center justify-center bg-teal-50 rounded-full">
              <i className="ri-user-line text-xs text-teal-600"></i>
            </span>
            <span className="text-xs text-gray-500">{post.author}</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-gray-400">
            <i className="ri-calendar-line"></i>
            <span>{post.date}</span>
          </div>
        </div>
        <button
          className="mt-4 flex items-center gap-1.5 text-teal-600 text-sm font-semibold hover:gap-3 transition-all cursor-pointer whitespace-nowrap"
          onClick={(e) => { e.stopPropagation(); onOpen(post); }}
        >
          Read full article <i className="ri-arrow-right-line"></i>
        </button>
      </div>
    </article>
  );
}

export default function BlogGrid() {
  const { posts: blogPosts } = useBlogPosts();
  const [selectedPost, setSelectedPost] = useState<BlogPost | null>(null);
  const [activeCategory, setActiveCategory] = useState("All");

  const categories = ["All", ...Array.from(new Set(blogPosts.map((p) => p.category)))];

  const filtered = activeCategory === "All"
    ? blogPosts
    : blogPosts.filter((p) => p.category === activeCategory);

  return (
    <section className="py-16 px-4 md:px-6 relative overflow-hidden"
      style={{ background: "linear-gradient(150deg, #e8fafa 0%, #d8f6f4 40%, #e0f8f2 70%, #edfcf9 100%)" }}
    >
      {/* Soft background blobs */}
      <div className="absolute top-0 right-0 w-80 h-80 rounded-full pointer-events-none opacity-30"
        style={{ background: "radial-gradient(circle, rgba(6,182,212,0.25) 0%, transparent 70%)", transform: "translate(30%,-30%)" }} />
      <div className="absolute bottom-0 left-0 w-64 h-64 rounded-full pointer-events-none opacity-25"
        style={{ background: "radial-gradient(circle, rgba(20,184,166,0.25) 0%, transparent 70%)", transform: "translate(-25%,25%)" }} />
      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Category filter */}
        <div className="flex flex-wrap gap-2 mb-10">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeCategory === cat
                  ? "bg-teal-600 text-white"
                  : "bg-white text-gray-600 border border-gray-200 hover:border-teal-300"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Featured post */}
        {filtered.length > 0 && (
          <div className="mb-8">
            <BlogCard post={filtered[0]} featured onOpen={setSelectedPost} />
          </div>
        )}

        {/* Grid of remaining posts */}
        {filtered.length > 1 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.slice(1).map((post) => (
              <BlogCard key={post.id} post={post} onOpen={setSelectedPost} />
            ))}
          </div>
        )}

        {filtered.length === 0 && (
          <div className="text-center py-20 text-gray-400">
            <i className="ri-article-line text-5xl mb-4 block"></i>
            <p>No articles in this category yet.</p>
          </div>
        )}
      </div>

      {selectedPost && (
        <BlogPostModal post={selectedPost} onClose={() => setSelectedPost(null)} />
      )}
    </section>
  );
}
